// Game state persistence service
import { Case, Proposal, VerifierResult } from '../types';

export interface GameState {
  currentCase: Case | null;
  proposals: Array<{ proposal: Proposal; results: VerifierResult[] }>;
  currentProposal: Proposal;
  showSolution: boolean;
  gameWon: boolean;
  crossedOffBeings: string[];
  crossedOffDevices: string[];
  crossedOffHideouts: string[];
  crossedOffClues: number[];
  turnCount: number;
  revealedClues: number[];
  fieldMemoRevealed: boolean;
  gameEnded: boolean;
  freeProposalUsed: boolean;
  failedProposals: number;
  clueOrder: number[];
  mobileShowEarlier: boolean;
  newGameMode: boolean;
  lastSaved: string;
}

export class GameStateService {
  private static readonly STORAGE_KEY = 'dimension23_game_state_v2';
  private static readonly MAX_SAVE_AGE_HOURS = 24; // Clear old saves after 24 hours

  static saveGameState(state: GameState): void {
    try {
      const stateToSave = {
        ...state,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save game state:', error);
    }
  }

  static loadGameState(): GameState | null {
    try {
      console.log('🔥🔥🔥 GameStateService.loadGameState() called 🔥🔥🔥');
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      console.log('localStorage.getItem result:', savedState ? 'has data' : 'null');
      if (!savedState) return null;

      const parsedState = JSON.parse(savedState) as GameState;
      console.log('Parsed state has lastSaved:', parsedState.lastSaved);
      
      // Check if save is too old
      if (parsedState.lastSaved) {
        const saveTime = new Date(parsedState.lastSaved);
        const now = new Date();
        const hoursDiff = (now.getTime() - saveTime.getTime()) / (1000 * 60 * 60);
        
        console.log('Save time check:', {
          saveTime: saveTime.toISOString(),
          now: now.toISOString(),
          hoursDiff: hoursDiff,
          maxAge: this.MAX_SAVE_AGE_HOURS,
          isTooOld: hoursDiff > this.MAX_SAVE_AGE_HOURS
        });
        
        if (hoursDiff > this.MAX_SAVE_AGE_HOURS) {
          console.log('Save is too old, clearing game state');
          this.clearGameState();
          return null;
        }
      }

      // Convert revealedClues back to Set if needed
      if (parsedState.revealedClues && Array.isArray(parsedState.revealedClues)) {
        // The Set will be reconstructed in the component
        return parsedState;
      }

      return parsedState;
    } catch (error) {
      console.warn('Failed to load game state:', error);
      this.clearGameState();
      return null;
    }
  }

  static clearGameState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear game state:', error);
    }
  }

  static hasSavedGame(): boolean {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) return false;

      const parsedState = JSON.parse(savedState) as GameState;
      
      // Check if save is too old
      if (parsedState.lastSaved) {
        const saveTime = new Date(parsedState.lastSaved);
        const now = new Date();
        const hoursDiff = (now.getTime() - saveTime.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff > this.MAX_SAVE_AGE_HOURS) {
          this.clearGameState();
          return false;
        }
      }

      return parsedState.currentCase !== null && !parsedState.gameEnded;
    } catch (error) {
      console.warn('Failed to check saved game:', error);
      return false;
    }
  }

  static getSavedGameInfo(): { date: string; caseId: string; turnCount: number } | null {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) return null;

      const parsedState = JSON.parse(savedState) as GameState;
      
      if (!parsedState.currentCase || parsedState.gameEnded) return null;

      return {
        date: parsedState.currentCase.incident?.date || 'unknown',
        caseId: parsedState.currentCase.case_id || 'unknown',
        turnCount: parsedState.turnCount || 0
      };
    } catch (error) {
      console.warn('Failed to get saved game info:', error);
      return null;
    }
  }
} 